import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import { getLoanETA, LoanETA, type Loan, type LoanType } from 'app/types/loan';
import type { SpecificNavigationProp } from 'app/types/navigation';
import { useTranslation } from 'react-i18next';
import { type LayoutChangeEvent, View } from 'react-native';

import { useCanUseServices } from 'app/network/category';
import { useUser } from 'app/network/user';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { useUISize } from 'app/utils/web-helpers';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconBell } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

export const useTPrefix = () => {
  const canUseServices = useCanUseServices();
  const { data: user } = useUser();
  const canUseDonations = Boolean(user?.canPublishDonations);
  const canPublish = isFeatureEnabled(FeatureFlag.publishListings, user?.featureFlags);

  const tPrefix =
    canUseServices && canUseDonations
      ? canPublish
        ? 'services+donations.'
        : 'services+donations-ro.'
      : canUseServices
        ? 'services.'
        : canUseDonations
          ? 'donations.'
          : '';

  return tPrefix;
};

export function ManagementTile({
  loans,
  navigation,
  type,
  selectedLoanType,
  height,
  onLayout,
  fillSpace = false,
}: {
  loans: Loan[] | undefined;
  navigation: SpecificNavigationProp<'management-overview'>;
  type: LoanType;
  /** When set (e.g. main area is `my-loans`), highlights the tile matching the active tab. */
  selectedLoanType?: LoanType;
  height?: number;
  onLayout?: (event: LayoutChangeEvent) => void;
  /** When true, the tile expands to fill available space (e.g. overview screen row). */
  fillSpace?: boolean;
}) {
  const { t } = useTranslation();
  const { isLargeUI } = useUISize();

  const tPrefix = useTPrefix();

  const doOnLayout = (event: LayoutChangeEvent) => {
    if (isLargeUI) return;
    onLayout?.(event);
  };

  const hasLateLoan = loans?.some((loan) => getLoanETA(loan) === LoanETA.late);

  const colorPerType = (() => {
    const isSelected = selectedLoanType === type;
    return {
      background: isSelected ? theme.colors['surface.brand.subtle'] : theme.colors['surface.default'],
      border: isSelected ? theme.colors['border.selected'] : theme.colors['surface.default'],
      borderWidth: 2,
    };
  })();

  const Wrapper = isLargeUI ? Row : Column;

  return (
    <View
      style={{
        ...(fillSpace ? { flex: 1 } : {}),
        minHeight: height,
        borderRadius: theme.borderRadii['radius.lg'],
        backgroundColor: colorPerType.background,
        borderColor: colorPerType.border,
        borderWidth: colorPerType.borderWidth,
        boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Button
        flex={1}
        onPress={() => navigation.navigate('my-loans', { type }, { pop: true })}
        padding="spacing.general.0"
        style={{ borderRadius: theme.borderRadii['radius.lg'] }}
      >
        <Wrapper
          onLayout={doOnLayout}
          gap="spacing.general.x2"
          borderRadius="radius.lg"
          height={height}
          flex={1}
          padding="spacing.general.x4"
          justifyContent="space-between"
        >
          <Column gap="spacing.general.x0,5" flex={7}>
            <Text fontSize={16} fontWeight="700" color="text.primary">
              {t(`${tPrefix}${type}.title`, { ns: 'loans' })}
            </Text>
            <Text fontSize={12}>{t(`${tPrefix}${type}.subtitle`, { ns: 'loans' })}</Text>
          </Column>
          <Row alignItems="flex-end" justifyContent="flex-end" gap="spacing.general.x2">
            <Text
              fontSize={16}
              fontWeight="700"
              color="text.primary"
              style={{
                backgroundColor: theme.colors['surface.tertiary'],
                paddingHorizontal: theme.spacing['spacing.general.x4'],
                paddingVertical: 5,
                borderRadius: theme.borderRadii['radius.xxl'],
              }}
            >
              {loans?.length ?? '-'}
            </Text>
            {hasLateLoan ? (
              <Row
                backgroundColor="surface.brand.secondary-4.default"
                borderRadius="radius.sm"
                alignItems="center"
                justifyContent="center"
                style={{ padding: 3 }}
              >
                <IconBell strokeWidth={ICON_STROKE_WIDTH} size={24} />
              </Row>
            ) : null}
          </Row>
        </Wrapper>
      </Button>
    </View>
  );
}
